import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { extractMergeFields } from '../constants';
import { useRenderTemplate } from '../queries';
import type { Template } from '../types';

interface RenderTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: Template | null;
  /** Called with the created document id after a successful render. */
  onRendered?: (documentId: number) => void;
}

export function RenderTemplateDialog({
  open,
  onOpenChange,
  template,
  onRendered,
}: RenderTemplateDialogProps) {
  const render = useRenderTemplate();
  const fields = useMemo(() => (template ? extractMergeFields(template.body) : []), [template]);

  const [name, setName] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});

  // Reset the form when a different template is opened — done during render
  // (React's "adjusting state on prop change" pattern) to avoid an effect.
  const formKey = open && template ? String(template.id) : null;
  const [activeKey, setActiveKey] = useState<string | null>(null);
  if (formKey !== activeKey) {
    setActiveKey(formKey);
    setName(template?.name ?? '');
    setValues(Object.fromEntries(fields.map((f) => [f, ''])));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!template) return;
    const doc = await render.mutateAsync({
      id: template.id,
      input: { context: values, name: name.trim() || undefined },
    });
    onOpenChange(false);
    onRendered?.(doc.id);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Render “{template?.name}”</DialogTitle>
          <DialogDescription>
            Fill the merge fields to create a document from this template.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="render-doc-name">Document name</Label>
            <Input
              id="render-doc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. NDA — Acme Corp"
            />
          </div>

          {fields.length ? (
            fields.map((f) => (
              <div key={f} className="space-y-2">
                <Label htmlFor={`mf-${f}`}>{f}</Label>
                <Input
                  id={`mf-${f}`}
                  value={values[f] ?? ''}
                  onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
                />
              </div>
            ))
          ) : (
            <p className="text-muted-foreground text-sm">
              This template has no merge fields — it will render as-is.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={render.isPending}>
              {render.isPending ? 'Rendering…' : 'Create document'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
